import { inject, Injectable } from '@angular/core';

import { Socket } from 'socket.io-client';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { routes } from '@libs/utils';
import { patientActions, selectSelectedPatientId, selectUserSiteId } from '@libs/store';
import { NotesScreenService } from '@libs/notes';
import { SchedulesService } from '@libs/schedule';
import { SocketIoService } from '@libs/shared-services';
import { FsaEvents } from './enums';
import { NotePayload } from './interfaces/note-payload.interface';
import { AppointmentPayload } from './interfaces/appointment-payload.interface';
import { PerioPayload } from './interfaces/perio-payload.interface';
import { TreatmentPayload } from './interfaces/treatment-payload.interface';
import { FsaServerEvents } from './interfaces/fsa-server-events.interface';

@Injectable({ providedIn: 'root' })
export class FsaListenerService extends SocketIoService<FsaServerEvents> {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly schedulesService = inject(SchedulesService);
  private readonly notesScreenService = inject(NotesScreenService);

  protected readonly namespace = '/fsa';
  protected readonly authentication = true;

  public connect(): void {
    this.startSocket();
  }

  protected setupListeners(socket: Socket<FsaServerEvents>): void {
    socket.on(FsaEvents.Note, (data: NotePayload) => {
      const selectedSiteId = this.store.selectSignal(selectUserSiteId)();
      const selectedPatientId = this.store.selectSignal(selectSelectedPatientId)();
      if (
        this.router.url.includes(routes.notes) &&
        data.status === 'COMPLETED' &&
        data.siteId === selectedSiteId &&
        data.patientId === selectedPatientId
      ) {
        this.notesScreenService.getVisits();
      }
    });
    socket.on(FsaEvents.Appointment, (data: AppointmentPayload) => {
      if (this.router.url.includes(routes.home) && data.status === 'COMPLETED') {
        this.schedulesService.handleSocketUpdate(data);
      }
    });
    socket.on(FsaEvents.Perio, (data: PerioPayload) => {
      if (this.router.url.includes(routes.perio) && data.status === 'COMPLETED') {
        const currentPatientId = this.store.selectSignal(selectSelectedPatientId)();
        const currentSiteId = this.store.selectSignal(selectUserSiteId)();
        if (data.siteId === currentSiteId && data.patientId && data.patientId === currentPatientId) {
          this.store.dispatch(
            patientActions.onLoadTeeth({
              siteId: data.siteId,
              patientId: data.patientId,
              examCount: 5,
              silentReload: true,
            }),
          );
        }
      }
    });
    socket.on(FsaEvents.Treatment, (data: TreatmentPayload) => {
      const currentPatientId = this.store.selectSignal(selectSelectedPatientId)();
      const currentSiteId = this.store.selectSignal(selectUserSiteId)();
      if (
        (this.router.url.includes(routes.charting) || this.router.url.includes(routes.notes)) &&
        data.status === 'COMPLETED' &&
        data.siteId === currentSiteId &&
        data.patientId &&
        data.patientId === currentPatientId
      ) {
        this.store.dispatch(
          patientActions.onLoadTeeth({
            siteId: data.siteId,
            patientId: data.patientId,
            examCount: 1,
            silentReload: true,
          }),
        );
        this.notesScreenService.getVisits();
      }
    });
    socket.on(FsaEvents.General, () => {
      console.warn('🚀 FSA General event received');
    });

    socket.on('connect', () => {
      console.warn('🚀 Connected to FSA socket');
    });

    socket.on('disconnect', () => {
      console.warn('🚀 Disconnected from FSA socket');
    });

    socket.on('connect_error', () => {
      console.warn('🚀 Connection error');
    });
  }
}
